/**
 * Gamepads outside the race: the room, the dialogs («Гонка», rooms, settings), the results,
 * the profile editor, the notices. The D-pad and the left stick move the focus (spatially,
 * input/focusNav.ts) inside the open dialog, else the screen; A presses the focused control;
 * B does what Back does (closes the open dialog, never leaves the app). A game screen reads
 * the pads itself and takes them first (input/GameInput.ts); it leaves them to this only while
 * a dialog of the shell covers it.
 *
 * The first press of a page load gets a toast: the pad is there, and what is what on it.
 */
import { ru } from '../i18n/ru';
import { moveFocus, navigationScope, pressFocused } from '../input/focusNav';
import { type GamepadHub, gamepads, type PadConsumer, type PadWindow } from '../input/gamepads';
import type { PadEdge } from '../input/padReader';
import { PAD, padDirection } from '../input/pads';
import type { ToastStore } from '../state/toasts';
import { backButton } from './backButton';

/** Long enough to read the legend on the toast. */
const CONNECTED_TOAST_MS = 6000;

export class PadNavigator implements PadConsumer {
  constructor(
    private readonly doc: Document,
    /** B: what Back does (see BackButton.press). */
    private readonly back: () => void,
  ) {}

  onPad(edge: PadEdge): boolean {
    if (!edge.down) return false;
    const direction = padDirection(edge.button);
    if (direction) {
      moveFocus(navigationScope(this.doc), direction);
      return true;
    }
    if (edge.repeat) return true;
    switch (edge.button) {
      case PAD.A:
        pressFocused(navigationScope(this.doc));
        return true;
      case PAD.B:
        this.back();
        return true;
      default:
        return false;
    }
  }
}

/** «A — прыжок, X и Y — бусты, …»: each part on one line, a line breaks only after a comma. */
function keepTogether(legend: string): string {
  return legend
    .split(', ')
    .map((part) => part.replace(/ /g, '\u00a0'))
    .join(', ');
}

/** Wires the app's gamepads to the page; returns the uninstall. */
export function installPadNavigation(
  toasts: ToastStore,
  win: PadWindow & { readonly document: Document } = window,
  pads: GamepadHub = gamepads,
): () => void {
  const off = [
    pads.install(win),
    pads.listen(new PadNavigator(win.document, () => backButton.press())),
    pads.onFirstUse((style) => {
      toasts.show(ru.gamepad.connected(keepTogether(ru.gamepad.legend[style])), {
        durationMs: CONNECTED_TOAST_MS,
      });
    }),
  ];
  return () => {
    for (const dispose of off) dispose();
  };
}
